import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, Timestamp, collection, query, where, getDocs, onSnapshot, addDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Role-based dashboard mapping
const getRoleDashboardPath = (role) => {
  const roleDashboardMap = {
    'admin': '/pulse-dashboard',
    'doctor': '/doctor-dashboard',
    'nurse': '/nursing-dashboard',
    'matron': '/nursing-dashboard',
    'lab_technician': '/laboratory-dashboard',
    'pharmacist': '/pharmacy-dashboard',
    'pharmacist_senior': '/pharmacy-dashboard',
    'pharmacy_manager': '/pharmacy-dashboard',
    'receptionist': '/patient-registration',
    'billing_clerk': '/accounts-dashboard',
    'accountant': '/accounts-dashboard',
    'finance_manager': '/accounts-dashboard',
    'auditor': '/accounts-dashboard',
    'admin_officer': '/duty-roster',
    'user': '/profile-setup'
  };

  return roleDashboardMap?.[role] || '/profile-setup'; // Default fallback
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [permissionsMap, setPermissionsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const presenceIntervalRef = useRef(null);
  const profileListenerRef = useRef(null);
  const loadedRoleRef = useRef(null);
  const heartbeatActiveRef = useRef(false);

  // Update user presence in Firestore
  const updatePresence = async (userId, isOnline = true) => {
    if (!userId) return;
    try {
      const userProfileRef = doc(db, 'user_profiles', userId);
      await updateDoc(userProfileRef, {
        is_online: isOnline,
        last_seen: Timestamp.now()
      });
    } catch (error) {
      console.warn('Failed to update presence:', error.message);
    }
  };

  // Start presence heartbeat (every 2 minutes)
  const startPresenceHeartbeat = (userId) => {
    if (heartbeatActiveRef.current) return;

    // Clear any existing interval
    if (presenceIntervalRef.current) {
      clearInterval(presenceIntervalRef.current);
    }

    // Update immediately
    updatePresence(userId, true);

    // Then update every 2 minutes
    presenceIntervalRef.current = setInterval(() => {
      updatePresence(userId, true);
    }, 2 * 60 * 1000);

    heartbeatActiveRef.current = true;
  };

  // Stop presence heartbeat
  const stopPresenceHeartbeat = () => {
    if (presenceIntervalRef.current) {
      clearInterval(presenceIntervalRef.current);
      presenceIntervalRef.current = null;
    }
    heartbeatActiveRef.current = false;
  };

  // Isolated async operations for profile management
  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      console.log('AuthContext - Loading profile for user:', userId);
      setProfileLoading(true);
      try {
        const userProfileRef = doc(db, 'user_profiles', userId);

        // Unsubscribe from any existing listener
        if (profileListenerRef.current) {
          profileListenerRef.current();
          profileListenerRef.current = null;
        }

        // Set up real-time listener for user profile changes
        profileListenerRef.current = onSnapshot(userProfileRef, async (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();

            // If the user's status is inactive, terminated, or suspended, log them out immediately
            if (data.employment_status === 'inactive' || data.employment_status === 'terminated' || data.employment_status === 'suspended' || data.is_suspended) {
              console.warn('User status is inactive, terminated, or suspended. Logging out user:', userId);
              await firebaseSignOut(auth);
              setUser(null);
              profileOperations.clear();
              try {
                const reason = data.employment_status === 'suspended' || data.is_suspended ? 'suspended' : 'status_revoked';
                window.location.href = `/login?reason=${reason}`;
              } catch (_) {}
              return;
            }

            const profileWithUid = { ...data, uid: userId };
            setUserProfile(profileWithUid);

            // Load role permissions from Firestore
            await profileOperations.loadPermissions(profileWithUid.role);

            // Start presence tracking
            startPresenceHeartbeat(userId);
          } else {
            console.error('User profile document does not exist:', userId);
            setUserProfile({
              id: userId,
              role: 'user',  // Default role
              created_at: new Date().toISOString()
            });
            setPermissionsMap({});
          }
          setProfileLoading(false);
        }, (error) => {
          console.error('Profile snapshot listener error:', error);
          setProfileLoading(false);
        });

      } catch (error) {
        console.error('Profile load error for user', userId, ':', error);
        setUserProfile({
          id: userId,
          role: 'user',
          created_at: new Date().toISOString(),
          error: error.message
        });
        setPermissionsMap({});
        setProfileLoading(false);
      }
    },

    async loadPermissions(role) {
      if (!role) return;
      if (role === loadedRoleRef.current) {
        console.log('AuthContext - Permissions already loaded for role:', role);
        return;
      }
      try {
        // Admin gets universal access — no need to fetch from DB
        if (role === 'admin') {
          setPermissionsMap({ __admin__: true });
          loadedRoleRef.current = 'admin';
          return;
        }
        const permCol = collection(db, 'role_permissions');
        const q = query(permCol, where('role', '==', role));
        const snaps = await getDocs(q);
        const map = {};
        snaps.docs.forEach(d => {
          const data = d.data();
          if (data.module) {
            // Robustly extract permission booleans regardless of document shape:
            //   flat unprefixed: { view: true }
            //   flat prefixed:   { can_view: true }
            //   nested:          { permissions: { can_view: true } }
            const p = data.permissions || data; // unwrap nested if present
            const extract = (key) => !!(p[key] || p[`can_${key}`] || data[key] || data[`can_${key}`]);
            map[data.module] = {
              view: extract('view'),
              create: extract('create'),
              edit: extract('edit'),
              delete: extract('delete')
            };
          }
        });
        console.log('AuthContext - Permissions loaded for role:', role, map);
        setPermissionsMap(map);
        loadedRoleRef.current = role;
      } catch (error) {
        console.warn('Failed to load role permissions:', error.message);
        setPermissionsMap({});
      }
    },

    clear() {
      setUserProfile(null);
      setPermissionsMap({});
      setProfileLoading(false);
      stopPresenceHeartbeat();
      loadedRoleRef.current = null;
      if (profileListenerRef.current) {
        profileListenerRef.current();
        profileListenerRef.current = null;
      }
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);

      if (user) {
        profileOperations.load(user.uid);
      } else {
        profileOperations.clear();
      }
    });

    // Cleanup subscription and presence interval
    return () => {
      unsubscribe();
      stopPresenceHeartbeat();
    };
  }, []);

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if user status is inactive, terminated, or suspended
      const userProfileRef = doc(db, 'user_profiles', userCredential.user.uid);
      const profileSnap = await getDoc(userProfileRef);
      if (profileSnap.exists()) {
        const data = profileSnap.data();
        if (data.employment_status === 'inactive' || data.employment_status === 'terminated' || data.employment_status === 'suspended' || data.is_suspended) {
          await firebaseSignOut(auth);
          let errorMsg = 'Your account is inactive. Please contact your administrator.';
          if (data.employment_status === 'suspended' || data.is_suspended) {
            errorMsg = data.suspension_reason 
              ? `Your account has been suspended: ${data.suspension_reason}` 
              : 'Your account has been suspended. Please contact your administrator.';
          } else if (data.employment_status === 'terminated') {
            errorMsg = 'Your account has been terminated. Please contact your administrator.';
          }
          throw new Error(errorMsg);
        }
      }
      
      return { data: userCredential, error: null };
    } catch (error) {
      return {
        data: null,
        error: {
          message: error.message || 'Authentication failed. Please try again.'
        }
      };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      
      // Ensure user profile exists
      const userProfileRef = doc(db, 'user_profiles', userCredential.user.uid);
      const profileSnap = await getDoc(userProfileRef);
      if (!profileSnap.exists()) {
        await setDoc(userProfileRef, {
          id: userCredential.user.uid,
          email: userCredential.user.email,
          full_name: userCredential.user.displayName || 'User',
          role: 'user', // default role, requires admin promotion
          employment_status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      } else {
        const data = profileSnap.data();
        if (data.employment_status === 'inactive' || data.employment_status === 'terminated' || data.employment_status === 'suspended' || data.is_suspended) {
          await firebaseSignOut(auth);
          let errorMsg = 'Your account is inactive. Please contact your administrator.';
          if (data.employment_status === 'suspended' || data.is_suspended) {
            errorMsg = data.suspension_reason 
              ? `Your account has been suspended: ${data.suspension_reason}` 
              : 'Your account has been suspended. Please contact your administrator.';
          } else if (data.employment_status === 'terminated') {
            errorMsg = 'Your account has been terminated. Please contact your administrator.';
          }
          throw new Error(errorMsg);
        }
      }
      
      return { data: userCredential, error: null };
    } catch (error) {
      return {
        data: null,
        error: {
          message: error.message || 'Google authentication failed. Please try again.'
        }
      };
    }
  };

  const signOut = async () => {
    try {
      // Mark user as offline before signing out
      if (user?.uid) {
        await updatePresence(user.uid, false);
      }
      await firebaseSignOut(auth);
      setUser(null);
      profileOperations.clear();
      return { error: null };
    } catch (error) {
      return {
        error: {
          message: error.message || 'Sign out failed. Please try again.'
        }
      };
    }
  };

  // Get user role from profile
  const getUserRole = () => userProfile?.role || 'user';

  // Check if user has required role
  const hasRole = (requiredRole) => {
    const userRole = getUserRole();
    if (requiredRole === 'admin') return userRole === 'admin';
    return userRole === requiredRole || userRole === 'admin';
  };

  // Check if user has a specific permission for a module
  // Usage: hasPermission('pharmacy', 'view') or hasPermission('patients', 'edit')
  const hasPermission = (module, action = 'view') => {
    const userRole = getUserRole();
    // Admin always has full access
    if (userRole === 'admin' || permissionsMap.__admin__) return true;
    // Check the dynamic permissions map
    return !!permissionsMap[module]?.[action];
  };

  // Verify password for re-authentication (e.g. workstation screen unlock)
  const verifyPassword = async (password) => {
    try {
      if (!auth.currentUser || !auth.currentUser.email) {
        throw new Error('No active user found to re-authenticate.');
      }
      const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      return { success: true, error: null };
    } catch (error) {
      console.warn('Re-authentication failed:', error.message);
      let errorMsg = 'Invalid password. Please try again.';
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        errorMsg = 'Incorrect password. Please verify and try again.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMsg = 'Too many failed attempts. Please wait a moment or sign in again.';
      }
      return { success: false, error: errorMsg };
    }
  };

  // Record security audit log for session events
  const logSessionEvent = async (eventType, metadata = {}) => {
    try {
      if (!user?.uid) return;
      await addDoc(collection(db, 'session_audit_logs'), {
        user_id: user.uid,
        email: user.email || userProfile?.email || '',
        role: userProfile?.role || 'user',
        event: eventType,
        timestamp: Timestamp.now(),
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        ...metadata
      });
    } catch (error) {
      console.warn('Failed to record session audit log:', error.message);
    }
  };

  const value = {
    user,
    userProfile,
    permissionsMap,
    loading,
    profileLoading,
    signIn,
    signInWithGoogle,
    signOut,
    verifyPassword,
    logSessionEvent,
    getUserRole,
    hasRole,
    hasPermission,
    getRoleDashboardPath
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;